import { tmDark, tmLight, tmReportingDark, tmReportingLight } from "../themes";
import { Container } from "./container";
import { getThemeManager, Overlay, Theme as OGTheme, ThemeType, ThemeDefinition } from '@omegagrid/core';

const DEFAULT_ACCENT_COLOR = '#24959e'; //'#5ec7d1',

const detectColorMode = (): ThemeType => window?.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

export class Theme {

	_onColorModeChange = () => this.colorMode = detectColorMode();

	constructor(readonly container: Container) { }

	init() {
		// registration happens here, not in the constructor: touching getThemeManager
		// activates the og theme, and the container is also constructed on pages that
		// must keep their document unstyled (standalone extension pages)
		this.register(tmReportingLight);
		this.register(tmReportingDark);

		Overlay.defaultLoaderImage = `${this.container.options.staticUrl}/assets/loader.webp`;
		window?.matchMedia('(prefers-color-scheme: dark)').removeEventListener('change', this._onColorModeChange);
		window?.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', this._onColorModeChange);

		this.extend('og-light', tmLight);
		this.extend('og-dark', tmDark);

		this._activeName = this.container.settings.get('theme') || `rp-${detectColorMode()}`;
		this.refresh();
	}

	private _accentColor: string = DEFAULT_ACCENT_COLOR;
	private _colorMode: ThemeType;
	private _activeName: string;

	get manager() { return getThemeManager() }
	get accentColor() { return this._accentColor }
	get activeThemeName() { return this._activeName }
	get colorMode() { return this._colorMode }
	get activeTheme() { return this.manager.activeTheme }

	set accentColor(color: string) {
		this._accentColor = color;
		this.refresh();
	}

	set colorMode(mode: ThemeType) {
		const name = this._activeName?.replace(/^(\w+)-(light|dark)$/, `$1-${mode}`);
		this.activate(name);
	}

	toggleColorMode() {
		this.colorMode = this.colorMode === 'light' ? 'dark' : 'light';
	}

	refresh() {
		document.body.removeAttribute('style');
		this.activate(this._activeName);
	}

	activate(name: string) {
		const theme = this.manager.themes.get(name);
		if (!theme) {
			this.container.log.error(`Theme ${name} not found`);
			return;
		}

		this._activeName = name;
		this._colorMode = theme.type;
		theme.accentColor = this._accentColor;
		this.manager.activate(name);
	}

	register(theme: OGTheme) {
		this.manager.registerTheme(theme);
	}

	extend(name: string, definition: ThemeDefinition) {
		const base = this.manager.themes.get(name);
		if (!base) {
			this.container.log.error(`Base theme ${name} not found`);
			return;
		}
		base.definition = { ...base.definition, ...definition };
		if (name == this._activeName) this.refresh();
	}

}
